import liff from '@line/liff'
import { useAppStateStore } from '~~/store/appState'
import { useEventStore } from '~~/store/event'

// รูปโปรไฟล์ LINE (ปุ่ม "รูป Line Profile" ในหน้าอัปโหลด) - ไม่มี backend
//
// 1. มี session LINE อยู่แล้ว -> liff.getProfile() ได้รูปทันที
// 2. ไม่มี session -> ปุ่มเป็น <a> ไปหน้า login LINE (สร้าง URL ไว้ล่วงหน้า ให้ iOS นับเป็น user tap)
//    - iOS external browser: access-auto.line.me (เด้งเข้าแอป LINE แล้วกลับมาเปิดแท็บใหม่)
//    - Android / ใน LINE: liff/v1/authorize (แบบเดียวกับ liff.login())
// 3. LINE ส่งกลับมาที่ /views/line-callback.html (public/views/line-callback.html, ผ่าน public/js/lineCallbackRedirect.js)
//    -> getProfile -> เก็บ pictureUrl ไว้ใน localStorage (กล่องจดหมาย) + BroadcastChannel
//    -> แท็บใหม่: ปิดตัวเอง แท็บแรกรับรูป / แท็บเดิม (iOS เว็บ, ใน LINE): กลับไปหน้าแรก -> พาเข้าหน้าอัปโหลด
//
// key / LIFF ID ต้องตรงกับ public/views/line-callback.html

const LIFF_IDS = {
  production: '2004940069-ApMgwb8O',
  beta: '2004940069-6dL72NYd',
  test: '2004940069-z1DopqLb',
  preview: '2004940069-YNwNLlqj',
}
const CALLBACK_PATH = '/views/line-callback.html'
const LIFF_ID_KEY = 'lineCallbackLiffId'
const REQUEST_KEY = 'lineProfileRequest'         // localStorage: { requestId, eventId, returnUrl, at }
const REQUEST_TAB_KEY = 'lineProfileRequestTab'  // sessionStorage: requestId ของแท็บที่กดปุ่ม
const RESULT_KEY = 'lineProfileResult'           // localStorage: { pictureUrl, requestId, at }
const TAB_ID_KEY = 'appTabId'                    // sessionStorage (public/js/singleTab.js)
const LATEST_TAB_KEY = 'appLatestTabId'          // localStorage (public/js/singleTab.js)
const CHANNEL_NAME = 'line-profile'
const RESULT_EXPIRE = 10 * 60 * 1000
const UPLOAD_PAGE = 'selectUploadAndWish'
const MOCK_PICTURE = '/pw-favicon.png'

const log = (...args: any[]) => console.log('[lineProfile]', ...args)

// ---------- env ----------
export const isLineProfileMock = () => window.location.hostname === 'localhost'

const getLiffId = () => {
  const host = window.location.hostname
  if (host.includes('wish-v2-test') || host.includes('localhost')) return LIFF_IDS.test
  if (host.includes('wish-v2-preview')) return LIFF_IDS.preview
  if (host.includes('wish-v2b')) return LIFF_IDS.beta
  return LIFF_IDS.production
}

const isIOS = () => {
  const userAgent = navigator.userAgent
  return /iPhone|iPad|iPod/i.test(userAgent) || (/Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1)
}

const isInLineApp = () => /Line\//i.test(navigator.userAgent)

// LIFF เก็บ token/codeVerifier ใน sessionStorage เมื่ออยู่ใน LIFF client (Android LINE) นอกนั้น localStorage
const getLiffStorage = () => (liff.isInClient() ? window.sessionStorage : window.localStorage)

const randomString = (length: number) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  const values = crypto.getRandomValues(new Uint8Array(length))
  return Array.from(values, (value) => chars[value % chars.length]).join('')
}

const readJson = (storage: Storage, key: string) => {
  try {
    return JSON.parse(storage.getItem(key) || 'null')
  } catch (error) {
    return null
  }
}

// มีแท็บที่เปิดใหม่กว่า -> แท็บนี้กำลังจะถูกเปลี่ยนไป Google (public/js/singleTab.js)
const isInactiveTab = () => {
  const tabId = sessionStorage.getItem(TAB_ID_KEY)
  const latestTabId = localStorage.getItem(LATEST_TAB_KEY)
  return !!tabId && !!latestTabId && tabId !== latestTabId
}

// ---------- session ----------
export const hasLineSession = () => {
  return !!getLiffStorage().getItem(`LIFF_STORE:${getLiffId()}:accessToken`)
}

let initPromise: Promise<void> | null = null
const initLiff = () => {
  if (!initPromise) {
    initPromise = liff.init({ liffId: getLiffId() }).catch((error) => {
      initPromise = null
      throw error
    })
  }
  return initPromise
}

// มี session -> pictureUrl ('' = ไม่มีรูปโปรไฟล์), ใช้ไม่ได้ (token หมดอายุ ฯลฯ) -> null
export const getLineProfilePictureFromSession = async (): Promise<string | null> => {
  try {
    await initLiff()
    if (!liff.isLoggedIn()) return null
    const profile = await liff.getProfile()
    return profile.pictureUrl || ''
  } catch (error) {
    log('session error', error)
    return null
  }
}

// ทดสอบ: ลบ session LINE (components/Line/LogoutTestButton.vue)
export const logoutLine = async () => {
  if (!hasLineSession()) return
  try {
    await initLiff()
    liff.logout()
  } catch (error) {
    log('logout error', error)
  }
  // init ไม่สำเร็จ -> ลบ token ที่ LIFF เก็บไว้เอง
  const storage = getLiffStorage()
  const prefix = `LIFF_STORE:${getLiffId()}:`
  Object.keys(storage)
    .filter((key) => key.startsWith(prefix))
    .forEach((key) => storage.removeItem(key))
}

// ---------- login link ----------
// สร้าง URL แบบเดียวกับ liff.login() ไว้ล่วงหน้า (PKCE ต้อง await) เพื่อให้ปุ่มเป็น <a> ที่แตะแล้วไปทันที
const buildLoginLink = async () => {
  const liffId = getLiffId()
  const codeVerifier = randomString(43)
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier))
  const codeChallenge = btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
  const origin = window.location.origin
  const params: Record<string, string> = {
    app_id: liffId,
    state: randomString(12),
    response_type: 'code',
    code_challenge_method: 'S256',
    code_challenge: codeChallenge,
    liff_sdk_version: liff.getVersion(),
  }
  const toQuery = (data: Record<string, string>) => Object.entries(data)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&')

  // iOS external browser: ลิงก์ "เข้าสู่ระบบด้วยแอป LINE" ของหน้า login LINE (universal link -> แอป LINE)
  // returnUri เลียนแบบหน้า consent ที่ liff/v1/authorize พาไป (redirect_uri = Endpoint URL + liff.state)
  if (isIOS() && !isInLineApp()) {
    const consentPath = `/oauth2/v2.1/authorize/consent?${toQuery({
      ...params,
      client_id: liffId.split('-')[0],
      scope: 'openid profile',
      type: 'L',
      redirect_uri: `${origin}?liff.state=${encodeURIComponent(CALLBACK_PATH)}`,
    })}`
    return {
      url: `https://access-auto.line.me/oauth2/v2.1/login?returnUri=${encodeURIComponent(consentPath)}&loginChannelId=${liffId.split('-')[0]}`,
      codeVerifier,
    }
  }

  return {
    url: `https://access.line.me/liff/v1/authorize?${toQuery({ ...params, redirect_uri: `${origin}${CALLBACK_PATH}` })}`,
    codeVerifier,
  }
}

// กลับมาแท็บเดิม (iOS login ผ่านเว็บ / ใน LINE) -> หน้าแรก แล้วพาเข้าหน้าอัปโหลด
const getReturnUrl = () => {
  const eventStore = useEventStore()
  const params = new URLSearchParams({ eventId: eventStore.eventId })
  if (eventStore.qrcodeId) {
    params.set('qrId', eventStore.qrcodeId)
  }
  return `${window.location.origin}/?${params.toString()}`
}

// เรียกตอนแตะลิงก์ (sync เท่านั้น ห้าม await ไม่อย่างนั้น iOS ไม่เปิดแอป LINE)
const saveLoginRequest = (codeVerifier: string) => {
  const liffId = getLiffId()
  const requestId = randomString(8)
  // ตำแหน่งเดียวกับที่ liff.login() เก็บ ให้ liff.init() ในหน้า callback แลก code ได้
  getLiffStorage().setItem(`LIFF_STORE:${liffId}:loginTmp`, JSON.stringify({ codeVerifier }))
  localStorage.setItem(LIFF_ID_KEY, liffId)
  localStorage.setItem(REQUEST_KEY, JSON.stringify({
    requestId,
    eventId: useEventStore().eventId,
    returnUrl: getReturnUrl(),
    at: Date.now(),
  }))
  sessionStorage.setItem(REQUEST_TAB_KEY, requestId)
  localStorage.removeItem(RESULT_KEY)
  log('login', { requestId })
}

export const useLineProfileLogin = () => {
  const login = shallowRef<{ url: string, codeVerifier: string } | null>(null)

  const prepare = async () => {
    if (isLineProfileMock()) return
    try {
      login.value = await buildLoginLink()
    } catch (error) {
      log('build login link error', error)
    }
  }

  // ใช้กับ @click ของ <a :href="loginUrl">
  // return true = เปิดลิงก์ไป login LINE / false = ไม่ไป (ให้ใช้ session หรือ mock แทน)
  const onClickLoginLink = (event: Event) => {
    if (isLineProfileMock() || hasLineSession() || !login.value) {
      event.preventDefault()
      return false
    }
    saveLoginRequest(login.value.codeVerifier)
    // ครั้งหน้าใช้ codeVerifier ใหม่
    setTimeout(prepare, 1000)
    return true
  }

  // ยังสร้างลิงก์ไม่เสร็จ / session ใช้ไม่ได้ -> ไป login ด้วย JS (iOS external จะได้หน้า login แบบเว็บ)
  const loginWithRedirect = async () => {
    if (!login.value) {
      await prepare()
    }
    if (!login.value) return
    saveLoginRequest(login.value.codeVerifier)
    window.location.href = login.value.url
  }

  onMounted(prepare)

  return {
    loginUrl: computed(() => login.value?.url || '#'),
    onClickLoginLink,
    loginWithRedirect,
  }
}

export const getLineProfileMockPicture = () => `${window.location.origin}${MOCK_PICTURE}`

// ---------- รับผลจากหน้า callback ----------
const readResult = () => {
  const result = readJson(localStorage, RESULT_KEY)
  if (!result?.pictureUrl) return null

  if (Date.now() - (result.at || 0) > RESULT_EXPIRE) {
    localStorage.removeItem(RESULT_KEY)
    return null
  }
  // รับเฉพาะผลของแท็บที่กดปุ่ม
  if (!result.requestId || result.requestId !== sessionStorage.getItem(REQUEST_TAB_KEY)) {
    return null
  }
  // แท็บเก่าที่กำลังถูกเปลี่ยนไป Google ห้ามรับ (public/js/singleTab.js)
  if (isInactiveTab()) return null
  return result
}

export const hasPendingLineProfileResult = () => !!readResult()

const consumeResult = (): string | null => {
  const result = readResult()
  if (!result) return null
  localStorage.removeItem(RESULT_KEY)
  localStorage.removeItem(REQUEST_KEY)
  sessionStorage.removeItem(REQUEST_TAB_KEY)
  log('result', { requestId: result.requestId })
  return result.pictureUrl
}

// หน้าแรก: กลับมาจาก login ในแท็บเดิม -> พาเข้าหน้าอัปโหลด (return true = พาไปแล้ว)
// router ส่งมาจาก setup ของหน้า (ถูกเรียกหลัง await จึงใช้ useRouter() ในนี้ไม่ได้)
export const goToUploadPageForLineProfile = async (router: ReturnType<typeof useRouter>) => {
  if (!hasPendingLineProfileResult()) return false

  try {
    const appStateStore = useAppStateStore()
    const eventStore = useEventStore()

    await appStateStore.selectToPage(UPLOAD_PAGE)
    if (appStateStore.view?.path !== `/${UPLOAD_PAGE}`) {
      log('no upload page in flow')
      consumeResult()
      return false
    }

    await router.push(`/${UPLOAD_PAGE}?eventId=${eventStore.eventId}`)
    return true
  } catch (error) {
    log('go to upload page error', error)
    return false
  }
}

// หน้าอัปโหลด: รับ pictureUrl (ตอนเปิดหน้า + ตอนแท็บ callback ส่งมา) แล้วอัปโหลด
export const useLineProfileReceiver = (onPicture: (pictureUrl: string) => void) => {
  const appStateStore = useAppStateStore()
  let channel: BroadcastChannel | null = null

  const check = () => {
    // เข้าหน้านี้ตรง ๆ จาก URL -> layout จะ reload ไปหน้าแรก (หน้าแรกจะพากลับมาอีกที)
    if (!appStateStore.landedIndexPage) return
    const pictureUrl = consumeResult()
    if (pictureUrl) onPicture(pictureUrl)
  }

  const onStorage = (event: StorageEvent) => {
    if (event.key === RESULT_KEY && event.newValue) check()
  }

  const onVisibility = () => {
    if (document.visibilityState === 'visible') check()
  }

  onMounted(() => {
    if ('BroadcastChannel' in window) {
      channel = new BroadcastChannel(CHANNEL_NAME)
      channel.onmessage = check
    }
    window.addEventListener('storage', onStorage)
    window.addEventListener('pageshow', check)
    document.addEventListener('visibilitychange', onVisibility)
    setTimeout(check, 500)
  })

  onBeforeUnmount(() => {
    channel?.close()
    window.removeEventListener('storage', onStorage)
    window.removeEventListener('pageshow', check)
    document.removeEventListener('visibilitychange', onVisibility)
  })
}
